import React, { useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import {
  buildPerformanceChartSeries,
  buildPlaceholderPerformancePoints,
  formatTotalTaskDisplay,
  formatXAxisLabel,
  getSubsampledIndices,
  PERFORMANCE_CHART_SMALL_DOT_RADIUS_PX,
  resolveActualAndExpectedForTaskPercent,
} from "./DutPerformanceChartData";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
);

const GRID_MAJOR = "#A3A3A3";
const GRID_MINOR = "#BFBFBF66";

/**
 * Subsample only when point count exceeds this. 49 = max half-hour slots in a day (0…23.5 + 24).
 * At 14, ~19 API slots were reduced to ~10 visible dots/labels; raising shows full response detail.
 */
const MAX_CHART_MARKERS = 49;

/** Cumulative x labels under grid — Lufga 400 / 16.36px / #1B1B1B (Figma). */
const X_AXIS_LABEL_FONT_WEIGHT = 400;
const X_AXIS_LABEL_FONT_SIZE_PX = 16.36;
const X_AXIS_LABEL_FONT_FAMILY =
  '"Lufga", "Lufga-Regular", ui-sans-serif, system-ui, sans-serif';
const X_AXIS_LABEL_COLOR = "#1B1B1B";
const X_AXIS_LABEL_OFFSET_TOP = 10;
const X_AXIS_LABEL_LETTER_SPACING_PX = 0;

/** Baseline dotted-line hover tooltip (canvas). */
const BASELINE_HOVER_TOLERANCE_PX = 8;
const BASELINE_TOOLTIP_PAD_X = 10;
const BASELINE_TOOLTIP_PAD_Y = 8;
const BASELINE_TOOLTIP_FONT =
  '400 12px "Lufga", "Lufga-Regular", ui-sans-serif, system-ui, sans-serif';
const BASELINE_TOOLTIP_BG = "rgba(27, 27, 27, 0.92)";
const BASELINE_TOOLTIP_TEXT = "#FFFFFF";

/**
 * Fill is only the ribbon between the data line and `halfHourStages` (Chart.js `fill: { value }`).
 * A vertical gradient on the full chart height was wrong: fill also reached the x-axis, so every x
 * showed yellow above the baseline line and red below it at once. Here the painted region is only
 * between line and baseline, so at each x you see either yellow (above avg) or red (below avg).
 */
function createChartGradient(ctx, chart, baselineY) {
  const yScale = chart?.scales?.y;
  const ca = chart?.chartArea;
  if (!yScale || !ca || ca.bottom <= ca.top) return "#EDFF8C";
  const b = Number.isFinite(baselineY) ? baselineY : 0;
  const yBaseline = yScale.getPixelForValue(b);
  if (!Number.isFinite(yBaseline)) return "#EDFF8C";
  const areaH = ca.bottom - ca.top;
  const gradient = ctx.createLinearGradient(0, ca.top, 0, ca.bottom);
  const ratio = Math.max(0.02, Math.min(0.98, (yBaseline - ca.top) / areaH));
  const clamp = (v) => Math.max(0, Math.min(1, v));
  gradient.addColorStop(0, "#EDFF8C");
  gradient.addColorStop(clamp(ratio - 0.005), "#EDFF8C");
  gradient.addColorStop(clamp(ratio), "rgba(255, 67, 67, 0)");
  gradient.addColorStop(
    clamp(ratio + (1 - ratio) * 0.0661),
    "rgba(255, 67, 67, 0)",
  );
  gradient.addColorStop(clamp(ratio + (1 - ratio) * 0.8932), "#FF4343");
  gradient.addColorStop(1, "#FF4343");
  return gradient;
}

function drawBaselineHoverTooltip(
  ctx,
  chartArea,
  anchorX,
  anchorY,
  name,
  valueStr,
) {
  const text = `${name}: ${valueStr}`;
  ctx.save();
  ctx.font = BASELINE_TOOLTIP_FONT;
  const tw = ctx.measureText(text).width;
  const w = Math.ceil(tw + BASELINE_TOOLTIP_PAD_X * 2);
  const lineH = 16;
  const h = BASELINE_TOOLTIP_PAD_Y * 2 + lineH;
  let tx = anchorX - w / 2;
  let ty = anchorY - h - 10;
  tx = Math.max(chartArea.left + 4, Math.min(tx, chartArea.right - w - 4));
  ty = Math.max(chartArea.top + 4, ty);
  ctx.fillStyle = BASELINE_TOOLTIP_BG;
  const r = 6;
  ctx.beginPath();
  ctx.moveTo(tx + r, ty);
  ctx.lineTo(tx + w - r, ty);
  ctx.quadraticCurveTo(tx + w, ty, tx + w, ty + r);
  ctx.lineTo(tx + w, ty + h - r);
  ctx.quadraticCurveTo(tx + w, ty + h, tx + w - r, ty + h);
  ctx.lineTo(tx + r, ty + h);
  ctx.quadraticCurveTo(tx, ty + h, tx, ty + h - r);
  ctx.lineTo(tx, ty + r);
  ctx.quadraticCurveTo(tx, ty, tx + r, ty);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = BASELINE_TOOLTIP_TEXT;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText(text, tx + BASELINE_TOOLTIP_PAD_X, ty + h / 2);
  ctx.restore();
}

function scheduleBaselineHoverRedraw(chart) {
  if (chart._baselineHoverRaf != null) {
    cancelAnimationFrame(chart._baselineHoverRaf);
  }
  chart._baselineHoverRaf = requestAnimationFrame(() => {
    chart._baselineHoverRaf = null;
    chart.update("none");
  });
}

const performanceChartDecorations = {
  id: "performanceChartDecorations",
  afterInit(chart) {
    const handler = () => {
      chart.$performanceBaselineHover = null;
      scheduleBaselineHoverRedraw(chart);
    };
    chart.canvas.addEventListener("mouseleave", handler);
    chart._performanceBaselineMouseLeave = handler;
  },
  destroy(chart) {
    if (chart._performanceBaselineMouseLeave) {
      chart.canvas.removeEventListener(
        "mouseleave",
        chart._performanceBaselineMouseLeave,
      );
    }
  },
  beforeDatasetsDraw(chart) {
    const opts = chart.options.plugins?.performanceChartDecorations;
    if (!opts?.enabled) return;
    const meta = chart.getDatasetMeta(0);
    if (!meta?.data?.length) return;
    const indices =
      Array.isArray(opts.markerIndices) && opts.markerIndices.length
        ? opts.markerIndices
        : [...Array(meta.data.length).keys()];
    const { top, bottom } = chart.chartArea;
    const ctx = chart.ctx;
    ctx.save();
    indices.forEach((i, j) => {
      const el = meta.data[i];
      if (!el) return;
      const x = el.x;
      ctx.strokeStyle = j % 2 === 0 ? GRID_MAJOR : GRID_MINOR;
      ctx.lineWidth = 0.34;
      ctx.beginPath();
      ctx.moveTo(x, top);
      ctx.lineTo(x, bottom);
      ctx.stroke();
    });
    ctx.restore();
  },
  afterDraw(chart) {
    const opts = chart.options.plugins?.performanceChartDecorations;
    if (!opts?.enabled) return;
    const yScale = chart.scales.y;
    const baseline = opts.baselineY;
    if (!Number.isFinite(baseline)) return;
    const yPix = yScale.getPixelForValue(baseline);
    if (!Number.isFinite(yPix)) return;
    const left = chart.chartArea.left;
    const right = chart.chartArea.right;
    if (yPix < chart.chartArea.top || yPix > chart.chartArea.bottom) return;
    const ctx = chart.ctx;
    ctx.save();
    ctx.setLineDash([4, 4]);
    /* Visible against fill + x-axis; Figma “expected” line parallel to x-axis */
    ctx.strokeStyle = "rgba(0, 0, 0, 0.42)";
    ctx.lineWidth = 1.25;
    ctx.beginPath();
    ctx.moveTo(left, yPix);
    ctx.lineTo(right, yPix);
    ctx.stroke();
    ctx.restore();

    const hover = chart.$performanceBaselineHover;
    if (hover?.show && hover.name != null) {
      const ctx2 = chart.ctx;
      drawBaselineHoverTooltip(
        ctx2,
        chart.chartArea,
        hover.anchorX,
        hover.anchorY,
        hover.name,
        hover.valueStr,
      );
    }
  },
  /** Runs before built-in tooltip so we can hide point tooltip when hovering the baseline. */
  beforeEvent(chart, args) {
    const opts = chart.options.plugins?.performanceChartDecorations;
    if (!opts?.enabled) {
      chart.$performanceBaselineHover = null;
      return;
    }
    const baseline = opts.baselineY;
    const evt = args.event;
    const nativeType = evt?.native?.type ?? evt?.type;
    if (!evt || nativeType === "mouseout") {
      chart.$performanceBaselineHover = null;
      scheduleBaselineHoverRedraw(chart);
      return;
    }
    if (!Number.isFinite(baseline)) {
      chart.$performanceBaselineHover = null;
      scheduleBaselineHoverRedraw(chart);
      return;
    }
    const yScale = chart.scales.y;
    if (!yScale) return;
    const yPix = yScale.getPixelForValue(baseline);
    if (!Number.isFinite(yPix)) return;
    const { top, bottom, left, right } = chart.chartArea;
    const x = evt.x;
    const y = evt.y;
    const nearLine =
      x >= left &&
      x <= right &&
      y >= top &&
      y <= bottom &&
      Math.abs(y - yPix) <= BASELINE_HOVER_TOLERANCE_PX;
    const name = opts.baselineLabel ?? "Half-hour stages";
    const valueStr =
      opts.baselineValueText != null
        ? String(opts.baselineValueText)
        : String(baseline);
    if (nearLine) {
      chart.$performanceBaselineHover = {
        show: true,
        anchorX: x,
        anchorY: yPix,
        name,
        valueStr,
      };
    } else {
      chart.$performanceBaselineHover = null;
    }
    scheduleBaselineHoverRedraw(chart);
  },
};

/** Figma-style cumulative x labels centered under each vertical grid line (or subsampled if many points). */
const performanceChartXAxisLabels = {
  id: "performanceChartXAxisLabels",
  afterDraw(chart) {
    const opts = chart.options.plugins?.performanceChartXAxisLabels;
    if (!opts?.enabled) return;
    const meta = chart.getDatasetMeta(0);
    if (!meta?.data?.length) return;
    const raw = chart.data.datasets[0]?.data;
    if (!Array.isArray(raw) || !raw.length) return;
    const n = raw.length;
    const maxLabels = opts.maxLabels ?? MAX_CHART_MARKERS;
    const indices = getSubsampledIndices(n, maxLabels);
    const ctx = chart.ctx;
    const { left, right, bottom } = chart.chartArea;

    // ✅ Read actual screen width and pick font size accordingly
    const screenWidth = window.innerWidth;
    const resolvedFontSize =
      screenWidth >= 1280 && screenWidth < 1544
        ? 12
        : X_AXIS_LABEL_FONT_SIZE_PX; // 16.36px for 1544px and above

    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    if ("letterSpacing" in ctx) {
      ctx.letterSpacing = `${X_AXIS_LABEL_LETTER_SPACING_PX}px`;
    }
    // ✅ Use resolvedFontSize instead of the constant directly
    ctx.font = `${X_AXIS_LABEL_FONT_WEIGHT} ${resolvedFontSize}px ${X_AXIS_LABEL_FONT_FAMILY}`;
    ctx.fillStyle = X_AXIS_LABEL_COLOR;

    // ✅ Offset and label Y also use resolvedFontSize so spacing stays correct
    const labelY = bottom + X_AXIS_LABEL_OFFSET_TOP + resolvedFontSize / 2;

    const seenPx = new Set();
    const allowedSet =
      opts.allowedIndices != null ? new Set(opts.allowedIndices) : null;

    indices.forEach((i) => {
      if (allowedSet && !allowedSet.has(i)) return;
      const pt = meta.data[i];
      if (!pt) return;
      const pxKey = Math.round(pt.x * 10) / 10;
      if (seenPx.has(pxKey)) return;
      seenPx.add(pxKey);
      const cum =
        opts.cumulativeXValues?.[i] ?? raw[i]?.cumulativeX ?? raw[i]?.x;
      const label = formatXAxisLabel(cum);
      if (!label) return;
      const tw = ctx.measureText(label).width;
      const half = tw / 2;
      const x = Math.max(left + half + 2, Math.min(right - half - 2, pt.x));
      ctx.fillText(label, x, labelY);
    });
    ctx.restore();
  },
};

ChartJS.register(performanceChartDecorations, performanceChartXAxisLabels);

/** True when stage counts vary across slots — only then show yellow/red fill vs baseline. */
function seriesYValuesFluctuate(points) {
  if (!points?.length || points.length < 2) return false;
  const ys = points.map((p) => Number(p?.y)).filter((v) => Number.isFinite(v));
  if (ys.length < 2) return false;
  let minY = ys[0];
  let maxY = ys[0];
  for (let i = 1; i < ys.length; i += 1) {
    const v = ys[i];
    if (v < minY) minY = v;
    if (v > maxY) maxY = v;
  }
  return Math.abs(maxY - minY) > 1e-9;
}

function makeLineDataset(
  points,
  baselineY,
  markerIndices,
  showAreaFill,
  isPlaceholder = false,
) {
  const showDot = new Set(markerIndices);
  const dotRadius = (ctx) => {
    if (isPlaceholder) return 0;
    return showDot.has(ctx.dataIndex)
      ? PERFORMANCE_CHART_SMALL_DOT_RADIUS_PX
      : 0;
  };
  const dotHoverRadius = (ctx) => {
    if (isPlaceholder) return 0;
    return showDot.has(ctx.dataIndex) ? 4 : 0;
  };
  return {
    label: "Performance",
    data: points.map((p) => ({
      x: p.x,
      y: p.y,
      cumulativeX: p.cumulativeX,
    })),
    datalabels: { display: false },
    fill:
      !isPlaceholder && showAreaFill && Number.isFinite(baselineY)
        ? { value: baselineY }
        : false,
    backgroundColor: (context) => {
      if (!showAreaFill || isPlaceholder) return "transparent";
      const c = context.chart;
      const meta = c.getDatasetMeta(0);
      if (!meta.data.length) return "transparent";
      return createChartGradient(c.ctx, c, baselineY);
    },
    borderColor: isPlaceholder ? "rgba(0, 0, 0, 0.11)" : "transparent",
    borderWidth: isPlaceholder ? 1.25 : 0,
    pointBackgroundColor: isPlaceholder ? "transparent" : "#000000",
    pointBorderColor: isPlaceholder ? "transparent" : "#000000",
    pointRadius: dotRadius,
    pointHoverRadius: dotHoverRadius,
    pointBorderWidth: 0,
    tension: 0.3,
  };
}

/**
 * @param {object|null} props.payload — API body: { formattedReport, halfHourStages }
 *   (`totalExpectedStages` + rollup `totalCompletedStage` live under `formattedReport`.)
 */
export default function DutPerformanceChart({
  payload = null,
  loading = false,
}) {
  const halfHourStages = Number(payload?.halfHourStages);
  const baselineY = Number.isFinite(halfHourStages) ? halfHourStages : 0;

  const series = useMemo(() => {
    const fr = payload?.formattedReport ?? {};
    return buildPerformanceChartSeries(fr);
  }, [payload]);

  const { points, lastCumulativeX } = series;

  const hasRealData = points.length > 0;
  const chartPoints = useMemo(
    () => (points.length > 0 ? points : buildPlaceholderPerformancePoints(20)),
    [points],
  );
  const isPlaceholder = !hasRealData;

  const { actual, expected, percent, displayTotal } =
    resolveActualAndExpectedForTaskPercent(payload);

  const taskPercent = percent;

  const safePercent = Number.isFinite(taskPercent) ? taskPercent : 0;

  const remainderAboveHalf = safePercent >= 0;
  const markerIndices = useMemo(
    () => getSubsampledIndices(chartPoints.length, MAX_CHART_MARKERS),
    [chartPoints.length],
  );

  const showAreaFill = useMemo(
    () => !isPlaceholder && seriesYValuesFluctuate(points),
    [isPlaceholder, points],
  );

  const chartData = useMemo(
    () => ({
      datasets: [
        makeLineDataset(
          chartPoints,
          baselineY,
          markerIndices,
          showAreaFill,
          isPlaceholder,
        ),
      ],
    }),
    [chartPoints, baselineY, markerIndices, showAreaFill, isPlaceholder],
  );

  const yBounds = useMemo(() => {
    if (!chartPoints.length || isPlaceholder) return { min: -100, max: 300 };
    const ysList = chartPoints.map((p) => p.y);
    const rawMin = Math.min(...ysList, baselineY, 0);
    const rawMax = Math.max(...ysList, baselineY, 0);
    const span = rawMax - rawMin || 1;
    const pad = span * 0.08;
    let minOut = rawMin - pad;
    let maxOut = rawMax + pad;
    /* Figma: expected / half-hour line must sit inside the plot, not on the x-axis edge.
       When baseline equals data min or max, expand the scale so the dashed line is visible. */
    const b = Number.isFinite(baselineY) ? baselineY : 0;
    const eps = span * 1e-9 + 1e-9;
    const edgePad = Math.max(span * 0.14, 1);
    if (Math.abs(rawMin - b) < eps) minOut -= edgePad;
    if (Math.abs(rawMax - b) < eps) maxOut += edgePad;
    return { min: minOut, max: maxOut };
  }, [chartPoints, baselineY, isPlaceholder]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          bottom: chartPoints.length > 0 ? 44 : 0,
          left: 8,
          right: 28,
          top: 0,
        },
      },
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { display: false },
        datalabels: { display: false },
        tooltip: {
          enabled: !isPlaceholder,
          displayColors: false,
          filter: (tooltipItem) => {
            const ch = tooltipItem.chart;
            return !ch.$performanceBaselineHover?.show;
          },
          callbacks: {
            title: () => [],
            label: (ctx) => {
              const y = ctx.parsed?.y;
              if (y == null || !Number.isFinite(y)) return "";
              const n = Number(y);
              const disp =
                Math.abs(n - Math.round(n)) < 1e-6
                  ? String(Math.round(n))
                  : String(n);
              return `Stage count: ${disp}`;
            },
          },
        },
        performanceChartDecorations: {
          enabled: chartPoints.length > 0,
          baselineY,
          baselineLabel: "Half-hour stages",
          baselineValueText: formatTotalTaskDisplay(baselineY),
          markerIndices,
        },
        performanceChartXAxisLabels: {
          enabled: chartPoints.length > 0,
          maxLabels: MAX_CHART_MARKERS,
          cumulativeXValues: chartPoints.map((p) => p.cumulativeX),
          // ✅ Only show labels at even indices (0, 2, 4, ...)
          allowedIndices: chartPoints
            .map((_, i) => i)
            .filter((i) => i % 2 === 0),
        },
      },
      scales: {
        x: {
          type: "linear",
          grid: { display: false },
          border: { display: false },
          ticks: {
            display: false,
          },
        },
        y: {
          grid: { display: false },
          border: { display: false },
          ticks: { display: false },
          position: "left",
          min: yBounds.min,
          max: yBounds.max,
        },
      },
    }),
    [
      baselineY,
      chartPoints,
      isPlaceholder,
      markerIndices,
      yBounds.max,
      yBounds.min,
    ],
  );

  const totalDisplay = formatTotalTaskDisplay(displayTotal);
  // const pctValue =
  //   taskRemainder != null && Number.isFinite(taskRemainder)
  //     ? taskRemainder.toFixed(1)
  //     : "0";

  // const safePercent = Number.isFinite(taskPercent) ? taskPercent : 0;

  const pctDisplay = `${safePercent >= 0 ? "+" : ""}${safePercent.toFixed(1)}%`;
  return (
    <div className="bg-white rounded-xl p-3 2xl:p-6 flex flex-col lg:flex-row lg:items-end gap-4 lg:gap-6 border border-black">
      <div className="flex flex-row items-center gap-[30px] flex-shrink-0 xl:min-w-[240px]">
        <span className="font-lufga font-normal text-2xl lg:text-[28px] xl:text-[36.75px] leading-[100%] tracking-0 text-gray-900">
          {loading ? "…" : totalDisplay}
        </span>
        <div className="flex flex-col items-start">
          <span
            className={`min-w-[60px] lg:min-w-[71px] h-[20px] lg:h-[22px] rounded-full text-white flex items-center justify-center gap-[14.7px] px-[6.13px] font-lufga font-normal text-sm lg:text-[17.15px] leading-[100%] tracking-[-0.05em]
  ${safePercent >= 0 ? "bg-green-500" : "bg-[#FF4343]"}`}
          >
            {" "}
            <span>{remainderAboveHalf ? "↑" : "↓"}</span> {pctDisplay}
          </span>
          <span className="font-lufga font-medium text-sm lg:text-[16.48px] leading-[100%] tracking-[-0.75px] text-[#1B1B1B] mt-[10px]">
            Total Task
          </span>
        </div>
      </div>

      <div className="flex-1 min-w-0 min-h-[180px] lg:min-h-[200px] xl:min-h-[220px] bg-white rounded-lg flex flex-col">
        <div className="flex-1 min-h-0 w-full relative">
          <Line data={chartData} options={chartOptions} />
        </div>
        {isPlaceholder ? (
          <p className="mt-2 text-center text-xs font-lufga text-gray-500 leading-snug px-2">
            Preview — cumulative stages by time slot appear here when data is
            available
          </p>
        ) : null}
      </div>
    </div>
  );
}
